import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  MultipleChoiceForm,
  type ChoiceOption,
} from '@/components/ui/multiple-choice-form'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type ChoiceBlockPolicy = 'cta' | 'quiz-grade' | 'plan-ask'

/** Stable id for the Cursor-style free-text "Other" option on plan-ask. */
export const PLAN_ASK_OTHER_ID = '__other__'

export type ChoiceBlockSubmitPayload = {
  selectedIds: string[]
  otherText?: string
}

export type ChoiceBlockAnswered = {
  selectedIds: string[]
  summary: string
  otherText?: string
}

export type ChoiceBlockProps = {
  question?: string
  options: ChoiceOption[]
  policy: ChoiceBlockPolicy
  /** quiz-grade: known correct option ids (usually one) */
  correctIds?: string[]
  explanation?: string
  selectionMode?: 'single' | 'multiple'
  progressLabel?: string
  disabled?: boolean
  className?: string
  /** Visual density for MultipleChoiceForm (flash cards use comfortable) */
  density?: 'comfortable' | 'compact'
  /**
   * plan-ask only: when true, append an Other option with free-text textarea.
   * Opt-in (default false) so the agent controls when free-text is available.
   */
  allowOther?: boolean
  /**
   * plan-ask: when set (from message.askQuestion.answered or local submit),
   * collapse the live form into a compact status badge.
   */
  answered?: ChoiceBlockAnswered | null
  /** cta / quiz-grade single-select activate */
  onActivate?: (optionId: string) => void
  /** Fired once when quiz-grade reveals feedback (layout may grow) */
  onRevealed?: () => void
  /** plan-ask submit */
  onSubmit?: (payload: ChoiceBlockSubmitPayload) => void
  /** After quiz-grade reveal */
  onNext?: () => void
  nextLabel?: string
  submitLabel?: string
}

function hasOtherOption(options: ChoiceOption[]): boolean {
  return options.some(
    (o) =>
      o.id === PLAN_ASK_OTHER_ID ||
      o.id.toLowerCase() === 'other' ||
      o.label.trim().toLowerCase() === 'other',
  )
}

/**
 * Chat attachment wrapper around MultipleChoiceForm with outcome policies.
 */
export function ChoiceBlock({
  question,
  options,
  policy,
  correctIds = [],
  explanation,
  selectionMode = 'single',
  progressLabel,
  disabled = false,
  className,
  density = 'compact',
  allowOther = false,
  answered = null,
  onActivate,
  onRevealed,
  onSubmit,
  onNext,
  nextLabel = 'Next',
  submitLabel = 'Continue',
}: ChoiceBlockProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [revealed, setRevealed] = useState(false)
  const [otherText, setOtherText] = useState('')
  const [localAnswered, setLocalAnswered] = useState<ChoiceBlockAnswered | null>(null)

  const effectiveAnswered = answered ?? localAnswered

  const effectiveOptions = useMemo(() => {
    if (policy !== 'plan-ask' || !allowOther || hasOtherOption(options)) {
      return options
    }
    return [
      ...options,
      {
        id: PLAN_ASK_OTHER_ID,
        label: 'Other',
        description: 'Describe your own approach',
      },
    ]
  }, [allowOther, options, policy])

  const otherSelected = selected.includes(PLAN_ASK_OTHER_ID) ||
    selected.some((id) => {
      const opt = effectiveOptions.find((o) => o.id === id)
      return (
        id.toLowerCase() === 'other' ||
        opt?.label.trim().toLowerCase() === 'other'
      )
    })

  const canSubmit =
    selected.length > 0 &&
    (!otherSelected || otherText.trim().length > 0)

  const handleActivate = (id: string) => {
    if (policy === 'quiz-grade') {
      setSelected([id])
      setRevealed(true)
      onActivate?.(id)
      onRevealed?.()
      return
    }
    if (policy === 'cta') {
      setSelected([id])
      onActivate?.(id)
      return
    }
    // plan-ask single: select only; submit via button
    setSelected([id])
  }

  const handleChange = (ids: string[]) => {
    setSelected(ids)
  }

  const handleSubmit = () => {
    if (!canSubmit || effectiveAnswered) return
    const labels = effectiveOptions
      .filter((o) => selected.includes(o.id))
      .map((o) => o.label)
    const otherTrimmed = otherSelected ? otherText.trim() : undefined
    const parts = [...labels]
    if (otherTrimmed) {
      const withoutBareOther = parts.filter(
        (l) => l.trim().toLowerCase() !== 'other',
      )
      withoutBareOther.push(otherTrimmed)
      parts.length = 0
      parts.push(...withoutBareOther)
    }
    const summary = parts.join(', ')
    const payload: ChoiceBlockSubmitPayload = {
      selectedIds: selected,
      otherText: otherTrimmed,
    }
    setLocalAnswered({
      selectedIds: selected,
      summary,
      otherText: otherTrimmed,
    })
    onSubmit?.(payload)
  }

  if (policy === 'plan-ask' && effectiveAnswered) {
    return (
      <div
        className={cn(
          'mt-2 inline-flex max-w-full items-start gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground',
          className,
        )}
        data-testid={testIds.choiceBlock.answered}
        role="status"
      >
        <Check
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/70"
          aria-hidden
        />
        <div className="min-w-0 space-y-0.5">
          {question ? (
            <p className="text-xs text-muted-foreground/80 line-clamp-2">
              {question}
            </p>
          ) : null}
          <p className="text-foreground/90 break-words">
            <span className="text-muted-foreground">Chose: </span>
            {effectiveAnswered.summary}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'mt-2 rounded-md border border-border bg-background/80 space-y-3',
        density === 'comfortable' ? 'p-4 sm:p-5 space-y-4' : 'p-3',
        className,
      )}
      data-testid={testIds.choiceBlock.root}
    >
      {progressLabel ? (
        <p
          className={cn(
            'text-muted-foreground tabular-nums',
            density === 'comfortable' ? 'text-sm font-medium' : 'text-xs',
          )}
          data-testid={testIds.choiceBlock.progress}
        >
          {progressLabel}
        </p>
      ) : null}

      <MultipleChoiceForm
        question={question}
        options={effectiveOptions}
        selectionMode={policy === 'plan-ask' ? selectionMode : 'single'}
        value={selected}
        revealed={
          policy === 'quiz-grade' && revealed
            ? { correctIds, explanation }
            : undefined
        }
        disabled={disabled || (policy === 'quiz-grade' && revealed)}
        density={density}
        onOptionActivate={
          policy === 'plan-ask' && selectionMode === 'multiple'
            ? undefined
            : handleActivate
        }
        onChange={
          policy === 'plan-ask' && selectionMode === 'multiple'
            ? handleChange
            : policy === 'plan-ask'
              ? handleChange
              : undefined
        }
      />

      {policy === 'plan-ask' && otherSelected ? (
        <Textarea
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Type your answer..."
          disabled={disabled}
          rows={3}
          className="min-h-[72px] text-sm resize-y"
          data-testid={testIds.choiceBlock.otherInput}
          autoFocus
        />
      ) : null}

      {policy === 'quiz-grade' && revealed && onNext ? (
        <Button
          type="button"
          size={density === 'comfortable' ? 'default' : 'sm'}
          className={density === 'comfortable' ? 'h-9 px-4' : 'h-8'}
          disabled={disabled}
          data-testid={testIds.choiceBlock.next}
          onClick={onNext}
        >
          {nextLabel}
        </Button>
      ) : null}

      {policy === 'plan-ask' ? (
        <Button
          type="button"
          size="sm"
          className="h-8"
          disabled={disabled || !canSubmit}
          data-testid={testIds.choiceBlock.submit}
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>
      ) : null}
    </div>
  )
}
